import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffAPI, classesAPI, uploadAPI, SITE_NAMES } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, User, Mail, Phone, GraduationCap } from 'lucide-react';

export const StaffAdmin = () => {
  const [staff, setStaff] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterSite, setFilterSite] = useState('all');
  const [formData, setFormData] = useState({
    name: '', position: '', email: '', phone: '', photo_url: '',
    class_id: '', class_name: '', site: 'ms', sort_order: 0, is_active: true
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [staffRes, classesMs, classesDs] = await Promise.all([
        staffAPI.getAll(null, false),
        classesAPI.getAll('ms', false),
        classesAPI.getAll('ds', false)
      ]);
      setStaff(staffRes.data);
      setClasses([...classesMs.data, ...classesDs.data]);
    } catch (error) {
      toast.error('Nepodarilo se nacíst data');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData({ name: '', position: '', email: '', phone: '', photo_url: '', class_id: '', class_name: '', site: 'ms', sort_order: 0, is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    setFormData({ ...member });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      setFormData(prev => ({ ...prev, photo_url: res.data.url }));
      toast.success('Fotka nahrána');
    } catch (error) {
      toast.error('Nepodarilo se nahrát fotku');
    } finally {
      setUploading(false);
    }
  };

  const handleClassChange = (classId) => {
    if (classId === 'none') {
      setFormData(prev => ({ ...prev, class_id: '', class_name: '' }));
    } else {
      const cls = classes.find(c => c.id === classId);
      setFormData(prev => ({ ...prev, class_id: classId, class_name: cls?.name || '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await staffAPI.update(editing.id, formData);
        toast.success('Zamestnanec aktualizován');
      } else {
        await staffAPI.create(formData);
        toast.success('Zamestnanec pridan');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Nepodarilo se uložit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tohoto zamestnance?')) return;
    try {
      await staffAPI.delete(id);
      toast.success('Zamestnanec smazán');
      loadData();
    } catch (error) {
      toast.error('Nepodarilo se smazat');
    }
  };

  const filteredStaff = filterSite === 'all' ? staff : staff.filter(s => s.site === filterSite);
  const siteClasses = classes.filter(c => c.site === formData.site);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Zamestnanci</h1>
          <p className="text-[#64748B]">Správa týmu a prirazení k trídám</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-staff-btn">
          <Plus size={18} className="mr-2" /> Pridat zamestnance
        </Button>
      </div>

      <div className="flex gap-2">
        {['all', 'ms', 'ds'].map(key => (
          <button
            key={key}
            onClick={() => setFilterSite(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filterSite === key
                ? 'bg-[#4ECDC4] text-white'
                : 'bg-white text-[#64748B] border border-slate-200 hover:border-[#4ECDC4]'
            }`}
            data-testid={`filter-${key}`}
          >
            {key === 'all' ? 'Všichni' : SITE_NAMES[key]}
          </button>
        ))}
      </div>

      {filteredStaff.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStaff.map((member) => (
            <div key={member.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden group" data-testid={`staff-item-${member.id}`}>
              <div className="aspect-square relative bg-slate-50">
                {member.photo_url ? (
                  <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={48} className="text-slate-300" />
                  </div>
                )}
                {!member.is_active && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">Neaktivní</div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" className="rounded-full" onClick={() => openEdit(member)} data-testid={`edit-staff-${member.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleDelete(member.id)} data-testid={`delete-staff-${member.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <div className="p-4">
                <div className="font-bold text-[#2D3748]">{member.name}</div>
                <div className="text-sm text-[#4ECDC4]">{member.position}</div>
                {member.class_name && (
                  <div className="text-xs text-[#64748B] mt-1 flex items-center gap-1">
                    <GraduationCap size={12} /> {member.class_name}
                  </div>
                )}
                <div className="text-xs text-[#64748B] mt-1">
                  {SITE_NAMES[member.site]}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <User size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-[#64748B]">Žádní zamestnanci</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Upravit zamestnance' : 'Nový zamestnanec'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Jméno a príjmení *</Label>
              <Input value={formData.name} onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))} required className="rounded-xl" data-testid="staff-name" />
            </div>

            <div className="space-y-2">
              <Label>Pozice *</Label>
              <Input value={formData.position} onChange={e => setFormData(prev => ({ ...prev, position: e.target.value }))} required placeholder="Trídní ucitelka, Reditelka..." className="rounded-xl" data-testid="staff-position" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input type="email" value={formData.email || ''} onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))} className="rounded-xl" data-testid="staff-email" />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={formData.phone || ''} onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="rounded-xl" data-testid="staff-phone" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Fotka</Label>
              <div className="flex gap-2 items-center">
                {formData.photo_url && (
                  <img src={formData.photo_url} alt="Preview" className="w-16 h-16 rounded-xl object-cover" />
                )}
                <label className="cursor-pointer flex-1">
                  <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                  <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                    <span><Upload size={16} className="mr-2" />{uploading ? 'Nahrávání...' : 'Nahrát fotku'}</span>
                  </Button>
                </label>
              </div>
              {formData.photo_url && (
                <Input value={formData.photo_url} onChange={e => setFormData(prev => ({ ...prev, photo_url: e.target.value }))} className="rounded-xl text-xs" placeholder="URL fotky" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sekce</Label>
                <Select value={formData.site} onValueChange={v => setFormData(prev => ({ ...prev, site: v, class_id: '', class_name: '' }))}>
                  <SelectTrigger className="rounded-xl" data-testid="staff-site"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ms">{SITE_NAMES.ms}</SelectItem>
                    <SelectItem value="ds">{SITE_NAMES.ds}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Trída</Label>
                <Select value={formData.class_id || 'none'} onValueChange={handleClassChange}>
                  <SelectTrigger className="rounded-xl" data-testid="staff-class"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Bez trídy</SelectItem>
                    {siteClasses.map(cls => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Poradí razení</Label>
                <Input type="number" value={formData.sort_order} onChange={e => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))} className="rounded-xl" data-testid="staff-sort" />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl mt-6">
                <Label className="text-sm">Aktivní</Label>
                <Switch checked={formData.is_active} onCheckedChange={v => setFormData(prev => ({ ...prev, is_active: v }))} data-testid="staff-active" />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Zrušit</Button>
              <Button type="submit" disabled={saving} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="save-staff-btn">
                {saving ? 'Ukládání...' : editing ? 'Uložit zmeny' : 'Pridat'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffAdmin;
